import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { Button } from '../../styles/components/Button';
import AnimatedBackground from '../../components/layout/AnimatedBackground/AnimatedBackground';
import { glassDeep, glassHover } from '../../styles/components/common/glassmorphism';

// Animation keyframes
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const fadeInOverlay = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Styled components for the registration page
const RegisterContainer = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  position: relative;
  font-family: 'Inter', 'Roboto', sans-serif;
  background-color: #121829;
  color: #ffffff;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
  z-index: 1;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const RegisterPanel = styled.div`
  width: 700px;
  background: rgba(30, 41, 59, 0.95);
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  max-width: 90%;
  position: relative;
  z-index: 5;
  margin: 2rem 0;
  color: #ffffff;
  animation: ${css`${floatAnimation} 6s infinite ease-in-out`};
  transition: all 0.3s ease;
  
  ${glassDeep}
  
  &:hover {
    ${glassHover}
    transform: translateY(-5px);
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    max-width: 95%;
  }
`;

const RegisterHeading = styled.h2`
  font-size: 1.8rem;
  color: #ffffff;
  margin-bottom: 1.5rem;
  font-weight: 600;
  text-align: center;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, 
      rgba(99, 102, 241, 0.8), 
      rgba(99, 102, 241, 0.3)
    );
    border-radius: 1px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  animation: ${css`${fadeIn} 0.5s ease-out forwards`};
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #ffffff;
  font-weight: 500;
  transition: color 0.3s ease;
  
  ${FormGroup}:hover & {
    color: #8C9EFF;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.9rem;
  background-color: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  font-size: 1rem;
  color: #ffffff;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: #4285F4;
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.3);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 0.9rem;
  background-color: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  font-size: 1rem;
  color: #ffffff;
  transition: all 0.3s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%238C9EFF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1em;
  
  &:focus {
    outline: none;
    border-color: #4285F4;
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.3);
  }
  
  option {
    background-color: #1E293B;
    color: #ffffff;
  }
`;

const StyledFileInput = styled.input`
  width: 100%;
  padding: 0.9rem;
  background-color: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  font-size: 1rem;
  color: #ffffff;
  transition: all 0.3s;
  
  &::-webkit-file-upload-button {
    background-color: #4285F4;
    color: white;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    margin-right: 1rem;
    cursor: pointer;
    transition: background-color 0.3s;
  }
  
  &::-webkit-file-upload-button:hover {
    background-color: #3367D6;
  }

  &:focus {
    outline: none;
    border-color: #4285F4;
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.3);
  }
`;

const SmallText = styled.small`
  display: block;
  margin-top: 0.25rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
`;

const ErrorMessage = styled.div`
  color: #ff5252;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  font-weight: 500;
  animation: ${css`${fadeIn} 0.3s ease-out forwards`};
`;

const RegisterButton = styled(Button)`
  width: 100%;
  padding: 0.8rem;
  border-radius: 8px;
  background: linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%);
  color: #FFFFFF;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 1.5rem;
  position: relative;
  overflow: hidden;
  
  &:hover {
    background: linear-gradient(135deg, #303F9F 0%, #3F51B5 100%);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.7s ease;
  }
  
  &:hover:before {
    left: 100%;
  }
`;

const PageTitle = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  background: linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%);
  color: #FFFFFF;
  font-weight: bold;
  padding: 0.5rem 1.5rem;
  border-radius: 20px;
  font-size: 1.2rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 10;
  animation: ${css`${floatAnimation} 6s infinite ease-in-out`};
`;

const TechBadge = styled.div`
  position: absolute;
  top: -12px;
  right: -12px;
  background: linear-gradient(135deg, #F4B400 0%, #FFCA28 100%);
  color: #1E1E1E;
  font-weight: bold;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 10;
`;

const OrbitingDot = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(99, 102, 241, 0.7);
  top: ${props => props.top};
  left: ${props => props.left};
  box-shadow: 0 0 10px 2px rgba(99, 102, 241, 0.3);
  animation: ${props => css`${floatAnimation} ${props.duration || '10s'} infinite linear`};
  z-index: 1;
`;

const AuthFooterWrapper = styled.div`
  text-align: center;
  margin-top: 1rem;
  color: #ffffff;
  
  .copyright {
    margin-top: 0.75rem;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
  }
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 1999;
  animation: ${fadeInOverlay} 0.3s ease-out forwards;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const scaleIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  60% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const SuccessIcon = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    width: 28px;
    height: 18px;
    border-left: 4px solid white;
    border-bottom: 4px solid white;
    transform: rotate(-45deg);
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid white;
  }
`;

// Popup animations
const popIn = keyframes`
  0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0; }
  70% { transform: translate(-50%, -50%) scale(1.05); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
  70% { box-shadow: 0 0 0 20px rgba(16, 185, 129, 0); }
  100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
`;

const floatingEffect = keyframes`
  0% { transform: translate(-50%, -50%); }
  50% { transform: translate(-50%, -53%); }
  100% { transform: translate(-50%, -50%); }
`;

const SuccessPopup = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(120deg, rgba(16, 185, 129, 0.85) 0%, rgba(5, 150, 105, 0.95) 100%);
  color: white;
  padding: 2.5rem 3rem;
  border-radius: 24px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.15) inset;
  z-index: 2000;
  text-align: center;
  animation: ${popIn} 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards,
             ${floatingEffect} 6s ease-in-out 0.5s infinite;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  width: 90%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
    font-weight: 700;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  p {
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.95);
    max-width: 90%;
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
    border-radius: inherit;
    z-index: -1;
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    width: 95%;
    
    h3 {
      font-size: 1.75rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const HomeButton = styled(Link)`
  position: absolute;
  top: 80px;
  left: 30px;
  z-index: 10;
  padding: 8px 16px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
    transform: translateY(-2px);
  }

  svg {
    margin-right: 8px;
  }
`;

const ConfirmButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 30px;
  padding: 0.8rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${pulseGlow} 2s infinite;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-3px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.7s ease;
  }
  
  &:hover:before {
    left: 100%;
  }
`;

const StyledLink = styled(Link)`
  color: #8C9EFF;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease;
  background-image: linear-gradient(to right, #8C9EFF, #8C9EFF);
  background-size: 0% 2px;
  background-position: 0 100%;
  background-repeat: no-repeat;
  
  &:hover {
    color: #C5CAE9;
    background-size: 100% 2px;
  }
`;

const FormDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  
  &:before, &:after {
    content: "";
    flex: 1;
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.2), transparent);
  }
  
  span {
    margin: 0 1rem;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
  }
`;

const CompanyRegister = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    companySize: '',
    email: '',
    password: '',
    logo: null,
    documents: []
  });
  
  const [errors, setErrors] = useState({
    logo: '',
    documents: ''
  });
  
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [redirectTimer, setRedirectTimer] = useState(null);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [id]: value
    }));
    
    // Clear error when field is changed
    if (errors[id]) {
      setErrors(prevState => ({
        ...prevState,
        [id]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const { id, files } = e.target;
    if (id === 'documents') {
      setFormData(prevState => ({
        ...prevState,
        [id]: Array.from(files)
      }));
    } else {
      setFormData(prevState => ({
        ...prevState,
        [id]: files[0]
      }));
    }
    
    // Clear error when file is uploaded
    if (errors[id]) {
      setErrors(prevState => ({
        ...prevState,
        [id]: ''
      }));
    }
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { ...errors };
    
    // Validate logo
    if (!formData.logo) {
      newErrors.logo = 'Please upload a company logo';
      valid = false;
    }
    
    // Validate documents
    if (!formData.documents.length) {
      newErrors.documents = 'Please upload at least one tax document';
      valid = false;
    }
    
    setErrors(newErrors);
    return valid;
  };

  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (validateForm()) {
      console.log('Form submitted:', formData);
      // Add your form submission logic here
      
      // Show success popup
      setShowSuccessPopup(true);
      
      // Redirect to login page after a delay (only if user doesn't click the button)
      const redirectTimer = setTimeout(() => {
        navigate('/login');
      }, 6000); // Extended to 6 seconds to give user time to read and click
      
      // Save the timer ID so we can clear it if user clicks the button
      setRedirectTimer(redirectTimer);
    } else {
      console.log('Form validation failed');
    }
  };
  
  const handleConfirmClick = () => {
    // Clear the auto-redirect timer
    if (redirectTimer) {
      clearTimeout(redirectTimer);
    }
    
    // Navigate to login page immediately
    navigate('/login');
  };

  return (
    <RegisterContainer>
      <HomeButton to="/">
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" 
            stroke="white" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
        Home
      </HomeButton>
      <AnimatedBackground />
      <OrbitingDot top="15%" left="15%" duration="8s" />
      <OrbitingDot top="70%" left="80%" duration="12s" />
      <OrbitingDot top="30%" left="75%" duration="10s" />
      
      <ContentWrapper>
        <PageTitle>InterConnect</PageTitle>
        <MainContent>
          <RegisterPanel>
            <TechBadge>Corporate</TechBadge>
            <RegisterHeading>Register Your Company</RegisterHeading>
            
            <form onSubmit={handleSubmit}>
              <FormGroup delay="0.1s">
                <Label htmlFor="companyName">Company Name</Label>
                <StyledInput 
                  type="text" 
                  id="companyName" 
                  required 
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Enter your company name"
                />
              </FormGroup>
              
              <FormGroup delay="0.2s">
                <Label htmlFor="industry">Industry</Label>
                <StyledSelect 
                  id="industry" 
                  required
                  value={formData.industry}
                  onChange={handleChange}
                >
                  <option value="">Select an industry</option>
                  <option value="technology">Technology</option>
                  <option value="finance">Finance</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="education">Education</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="retail">Retail</option>
                  <option value="other">Other</option>
                </StyledSelect>
              </FormGroup>
              
              <FormGroup delay="0.3s">
                <Label htmlFor="companySize">Company Size</Label>
                <StyledSelect 
                  id="companySize" 
                  required
                  value={formData.companySize}
                  onChange={handleChange}
                >
                  <option value="">Select company size</option>
                  <option value="small">Small (50 employees or less)</option>
                  <option value="medium">Medium (more than 50, less than or equal to 100 employees)</option>
                  <option value="large">Large (more than 100, less than or equal to 500 employees)</option>
                  <option value="corporate">Corporate (more than 500 employees)</option>
                </StyledSelect>
              </FormGroup>
              
              <FormDivider>
                <span>Account Details</span>
              </FormDivider>
              
              <FormGroup delay="0.4s">
                <Label htmlFor="email">Official Company Email</Label>
                <StyledInput 
                  type="email" 
                  id="email" 
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="company@yourdomain.com"
                />
              </FormGroup>
              
              <FormGroup delay="0.5s">
                <Label htmlFor="password">Password</Label>
                <StyledInput 
                  type="password" 
                  id="password" 
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a secure password"
                />
              </FormGroup>
              
              <FormDivider>
                <span>Company Verification</span>
              </FormDivider>
              
              <FormGroup delay="0.6s">
                <Label htmlFor="logo">Company Logo</Label>
                <StyledFileInput 
                  type="file" 
                  id="logo" 
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ borderColor: errors.logo ? '#ff5252' : '#ccc' }}
                />
                {errors.logo && <ErrorMessage>{errors.logo}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup delay="0.7s">
                <Label htmlFor="documents">Tax Documents</Label>
                <StyledFileInput 
                  type="file" 
                  id="documents" 
                  multiple 
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  style={{ borderColor: errors.documents ? '#ff5252' : '#ccc' }}
                />
                <SmallText>
                  Please upload documents proving your company is legitimate (such as tax documents)
                </SmallText>
                {errors.documents && <ErrorMessage>{errors.documents}</ErrorMessage>}
              </FormGroup>

              <RegisterButton type="submit">Join InterConnect</RegisterButton>
            </form>
            
            <AuthFooterWrapper>
                Already registered? <StyledLink to="/login">Sign in to your account</StyledLink>
                <div className="copyright">&copy; 2025 SCAD System. All rights reserved.</div>
            </AuthFooterWrapper>
          </RegisterPanel>
        </MainContent>
      </ContentWrapper>
      
      {showSuccessPopup && (
        <>
          <PopupOverlay />
          <SuccessPopup>
            <SuccessIcon />
            <h3>Registration Successful!</h3>
            <p>Your company has been registered successfully. You will now be redirected to the login page.</p>
            <ConfirmButton onClick={handleConfirmClick}>Go to Login</ConfirmButton>
          </SuccessPopup>
        </>
      )}
    </RegisterContainer>
  );
};

export default CompanyRegister;